import { t } from 'i18next';
import React, { useState, useEffect, useRef, ReactNode } from 'react';

interface FormSelectCreatableProps<T extends string> {
  label: string;
  labelElement?: ReactNode;
  value: T;
  onChange: (value: T) => void;
  options: { id: string; name: string }[];
  onCreateOption?: (option: string) => void;
  placeholder?: string;
  hideLabel?: boolean;
  createText?: string;
  disabled?: boolean;
}

export const FormSelectCreatable = <T extends string>({
  label,
  labelElement,
  value,
  onChange,
  options,
  onCreateOption,
  placeholder,
  hideLabel = false,
  createText,
  disabled = false,
}: FormSelectCreatableProps<T>) => {
  const [inputValue, setInputValue] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAdding]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;

    // If the user selects the "add new" option
    if (val === '__new__') {
      setIsAdding(true);
      return;
    }

    onChange(val as T);
  };

  const handleAddNew = () => {
    if (!inputValue.trim()) {
      setIsAdding(false);
      return;
    }

    if (onCreateOption) {
      onCreateOption(inputValue);
    }

    onChange(inputValue as T);
    setInputValue('');
    setIsAdding(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddNew();
    } else if (e.key === 'Escape') {
      setIsAdding(false);
      setInputValue('');
    }
  };

  return (
    <div>
      {labelElement ? (
        labelElement
      ) : (
        <label
          className={
            hideLabel ? 'sr-only' : 'block text-sm font-medium text-slate-700 dark:text-slate-200'
          }
        >
          {label}
        </label>
      )}

      {isAdding ? (
        <div className="mt-1 flex rounded-md shadow-sm">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="block w-full rounded-l-md border border-slate-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-surface-600 dark:bg-surface-800 dark:text-white sm:text-sm"
            placeholder={createText ?? t('common.add')}
          />
          <button
            type="button"
            onClick={handleAddNew}
            className="inline-flex items-center rounded-r-md border border-l-0 border-slate-300 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-surface-600 dark:bg-surface-700 dark:text-white dark:hover:bg-surface-600"
          >
            {createText ?? t('common.add')}
          </button>
        </div>
      ) : (
        <select
          value={value}
          onChange={handleChange}
          disabled={disabled}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-surface-600 dark:bg-surface-800 dark:text-white dark:hover:border-surface-500 sm:text-sm"
          aria-label={hideLabel ? label : undefined}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
          {onCreateOption && <option value="__new__">{createText ?? t('common.add')}</option>}
        </select>
      )}
    </div>
  );
};
